// src/Phaser/engine/entities/Object.ts
import Phaser from "phaser";
import type { PhaserScene } from "../PhaserScene";
import type { ObjectConfig } from "../../types/config";
import { setArcadeBodySize } from "../utils/physics";
import { PUSHABLE } from "../collision/CollisionSystem";

export type ObjectState = "idle" | "carried" | "thrown" | "pushed" | "obstacle";

const normKey = (s?: string) => String(s ?? "").trim().replace(/\s+/g, "_");
const withPrefixes = (k: string) => [`obj_${k}`, `sprite_${k}`, `obstacle_${k}`];

export class GameObject {
  private adminMode: boolean;
  public id: string;
  public idPhaser: string; // canonical key exposed to the rest of the code
  private scene?: Phaser.Scene;

  public x: number;
  public y: number;
  public width: number;
  public height: number;

  public immovable: boolean;
  public pickable: boolean;
  public pickOrder: number;
  public pushable: boolean;
  public decor: boolean;

  public currentState: ObjectState = "idle";

  // Phaser
  public sprite?: Phaser.Types.Physics.Arcade.SpriteWithDynamicBody;

  // Optional asset info from config (kept for parity)
  public texture: string;

  // Internals
  private adminLabel?: Phaser.GameObjects.Text;
  private boundUpdateForLabel?: () => void;
  private _isDestroying = false;
  private _resolvedTextureKey?: string; // effective texture key used by the sprite

  private placeholderTextureKey?: string;
  private textureAddHandler?: (key: string, texture: Phaser.Textures.Texture) => void;

  constructor(adminMode: boolean, config: ObjectConfig) {
    this.adminMode = adminMode;
    this.id = config.id;
    this.idPhaser = normKey(config.idPhaser ?? config.id);

    this.x = config.x;
    this.y = config.y;
    this.width = config.width;
    this.height = config.height;

    this.immovable = config.immovable ?? false;
    this.pickable = config.pickable ?? false;
    this.pickOrder = Number(config.pickOrder) || 0;
    this.pushable = config.pushable ?? false;
    this.decor = config.decor ?? false;

    if (this.decor) {
      // decor = décor fixe, pas ramassable
      this.immovable = true;
      this.pickable = false;
    }

    this.texture = config.assets.texture;

    if (this.immovable) {
      this.currentState = "obstacle";
    }
  }

  //  Pick a safe texture key to use. If `idPhaser` is missing but an
  //    `obj_*/sprite_*/obstacle_*` exists, create an alias under `idPhaser`
  //   to keep the rest of the code consistent.

  private resolveTextureOrPlaceholder(scene: Phaser.Scene): string {
    const tm = scene.textures;
    const wanted = normKey(this.idPhaser);
    if (tm.exists(wanted)) return wanted;

    // Génère un placeholder 8x8 si pas déjà créé
    if (!this.placeholderTextureKey) {
      this.placeholderTextureKey = `${wanted}__ph`;
      if (!tm.exists(this.placeholderTextureKey)) {
        const g = scene.add.graphics();
        g.fillStyle(0x6699ff, 1);
        g.fillRect(0, 0, 8, 8);
        g.generateTexture(this.placeholderTextureKey, 8, 8);
        g.destroy();
      }
    }

    // S’abonner: dès que la vraie texture arrive → basculer + recalibrer body
    if (!this.textureAddHandler) {
      this.textureAddHandler = (key: string) => {
        if (key !== wanted || !this.sprite || !this.sprite.scene) return;
        const sc = this.sprite.scene;
        sc.textures.off(Phaser.Textures.Events.ADD, this.textureAddHandler!);
        this.textureAddHandler = undefined;

        this.sprite.setTexture(wanted);
        this._resolvedTextureKey = wanted;   // ✅ on bascule définitivement sur la vraie clé

        // réapplique les tailles & body maintenant que l’image réelle est connue
        this.sprite.setDisplaySize(this.width, this.height);
        const body = this.sprite.body as Phaser.Physics.Arcade.Body | undefined;
        if (body) {
          body.setSize(
            this.sprite.displayWidth / (this.sprite.scaleX || 1),
            this.sprite.displayHeight / (this.sprite.scaleY || 1),
            false
          );
          body.setOffset(0, 0);
        }
      };

      scene.textures.on(Phaser.Textures.Events.ADD, this.textureAddHandler);
    }
    return this.placeholderTextureKey!;
  }


  /** Crée le sprite Phaser dans la scène. */
  public createPhaserSprite(scene: Phaser.Scene) {
    this.scene = scene;

    // Resolve a real texture key, or bail
    const key = this.resolveTextureOrPlaceholder(scene);
    if (!key) {
      // Avoid hard crash; let a subsequent handleObjects() retry after hot-load
      return;
    }
    this._resolvedTextureKey = key;

    const s = scene.physics.add.sprite(this.x, this.y, key);
    s.setOrigin(0, 0)
      .setDisplaySize(this.width, this.height)
      .setCollideWorldBounds(true)

    const body = s.body as Phaser.Physics.Arcade.Body;
    body.setDamping(false);
    body.setDrag(PUSHABLE.DRAG, PUSHABLE.DRAG);
    body.setMaxVelocity(PUSHABLE.MAX_VELOCITY, PUSHABLE.MAX_VELOCITY);
    body.setBounce(0, 0);

    // Body size matches display size (in local scale)
    const sx = s.scaleX || 1,
      sy = s.scaleY || 1;
    const unscaledW = s.displayWidth / sx;
    const unscaledH = s.displayHeight / sy;
    body.setSize(unscaledW, unscaledH, false);
    body.setOffset(0, 0);


    if (this.decor) {
      this.immovable = true;           // decor ⇒ immovable
      body.setImmovable(true);
      body.moves = false;               // statique (même si allowGravity=false)
    } else {
      body.setImmovable(!!this.immovable);
      body.moves = true;                // ✅ pour subir la gravité
    }
    // gravité décidée par la scène :
    body.allowGravity = (scene as any).computeAllowGravity?.(this) ?? false;

    this.sprite = s;

    s.setData("id", this.id);
    s.setData("kind", "object");
    s.setData("entity", this);
    s.setData("immovable", this.immovable);
    s.setData("pushable", this.pushable);
    s.setData("pickable", this.pickable);
    s.setData("flags", {
      immovable: this.immovable,
      pushable: this.pushable,
      pickable: this.pickable,
    });

    // Guarded cleanup: avoid double-destroy during scene teardown
    this.sprite.once("destroy", () => this.destroy(true));

    // Initial visual state
    this.setAnimation("idle");

    // Admin label (follow + cleanup)
    if (this.adminMode) {
      const idText = scene.add.text(s.x, s.y - 15, this.idPhaser, {
        fontSize: "12px",
        color: "#ffffff",
        backgroundColor: "#00000088",
      });
      idText.setOrigin(0.5, 1);
      this.adminLabel = idText;

      const update = () => {
        if (!this.sprite || !this.adminLabel) return;
        this.adminLabel.x = this.sprite.x + this.sprite.displayWidth / 2;
        this.adminLabel.y = this.sprite.y - 2;
        this.adminLabel.setDepth((this.sprite.depth ?? 0) + 2);
      };
      this.boundUpdateForLabel = update;
      scene.events.on("update", update);
    }
  }

  /** Synchronise x/y logiques depuis le sprite. */
  public updatePhaserSprite() {
    if (!this.sprite) return;
    this.x = this.sprite.x;
    this.y = this.sprite.y;
  }

  /** Pousser l’objet (si pushable). */
  public push(dx: number, dy: number) {
    if (!this.pushable || this.immovable) return;
    this.currentState = "pushed";

    this.x += dx;
    this.y += dy;
    this.sprite?.setPosition(this.x, this.y);

    this.currentState = "idle";
  }

  /** Ramasser l’objet en respectant l’ordre (si défini). */
  public pickUp(): boolean {
    if (!this.pickable || this.currentState !== "idle") return false;
    if (!this.scene || !this.sprite) return false;

    const mainScene = this.scene as PhaserScene;
    const expected =
      typeof (mainScene as any).getNextPickOrder === "function"
        ? (mainScene as any).getNextPickOrder()
        : 0;

    if (this.pickOrder > 0 && this.pickOrder !== expected) {
      console.log("pickUp blocked: order mismatch", {
        expected,
        pickOrder: this.pickOrder,
      });
      return false;
    }

    this.currentState = "carried";

    const startY = this.sprite.y;
    this.scene.tweens.add({
      targets: this.sprite,
      duration: 300,
      y: startY - 30,
      alpha: 0,
      ease: "Quad.easeIn",
      onComplete: () => {
        if (!this.sprite) return;
        this.sprite.visible = false;
        if (this.sprite.body) (this.sprite.body as Phaser.Physics.Arcade.Body).enable = false;
      },
    });

    // Système d'ordre
    if (
      typeof (mainScene as any).getObjectsByPickOrder === "function" &&
      typeof (mainScene as any).incrementPickOrder === "function"
    ) {
      const sameOrder = (mainScene as any).getObjectsByPickOrder(expected) as GameObject[];
      const allCarried = sameOrder.every((o) => o.currentState === "carried");
      if (allCarried) (mainScene as any).incrementPickOrder();
    }

    return true;
  }

  /** Bulle de dialogue au-dessus de l’objet. */
  public say(message: string, duration: number = 2000) {
    if (!this.scene || !this.sprite) return;

    const sceneWidth = this.scene.sys.canvas.width;
    const sceneHeight = this.scene.sys.canvas.height;

    const bubbleWidth = Math.max(100, message.length * 8 + 20);
    const bubbleHeight = 50;
    const arrowSize = 8;
    const margin = 6;

    const container = this.scene.add.container(0, 0);
    const bubbleGfx = this.scene.add.graphics();
    const textObj = this.scene.add
      .text(0, 0, message, {
        fontSize: "14px",
        color: "#000000",
        wordWrap: { width: bubbleWidth - 10 },
      })
      .setOrigin(0.5, 0.5);

    container.add([bubbleGfx, textObj]);

    const drawBubble = (arrowAtBottom: boolean) => {
      bubbleGfx.clear().lineStyle(2, 0x000000, 1).fillStyle(0xffffff, 1);
      if (arrowAtBottom) {
        bubbleGfx
          .fillRoundedRect(-bubbleWidth / 2, -bubbleHeight, bubbleWidth, bubbleHeight, 10)
          .strokeRoundedRect(-bubbleWidth / 2, -bubbleHeight, bubbleWidth, bubbleHeight, 10)
          .fillTriangle(-arrowSize, 0, arrowSize, 0, 0, arrowSize)
          .strokeTriangle(-arrowSize, 0, arrowSize, 0, 0, arrowSize);
        textObj.setPosition(0, -bubbleHeight / 2);
      } else {
        bubbleGfx
          .fillRoundedRect(-bubbleWidth / 2, 0, bubbleWidth, bubbleHeight, 10)
          .strokeRoundedRect(-bubbleWidth / 2, 0, bubbleWidth, bubbleHeight, 10)
          .fillTriangle(-arrowSize, 0, arrowSize, 0, 0, -arrowSize)
          .strokeTriangle(-arrowSize, 0, arrowSize, 0, 0, -arrowSize);
        textObj.setPosition(0, bubbleHeight / 2);
      }
    };

    const updatePos = () => {
      if (!this.sprite) return;

      let cx = this.sprite.getCenter().x;
      let cy = this.sprite.y - margin;
      let arrowBottom = true;

      if (cy - bubbleHeight < 0) {
        arrowBottom = false;
        cy = this.sprite.y + this.sprite.displayHeight + margin;
      }

      const halfW = bubbleWidth / 2;
      if (cx - halfW < 0) cx = halfW;
      else if (cx + halfW > sceneWidth) cx = sceneWidth - halfW;

      if (!arrowBottom && cy + bubbleHeight > sceneHeight) {
        arrowBottom = true;
        cy = this.sprite.y - margin;
      }

      drawBubble(arrowBottom);
      container.setPosition(cx, cy);
      container.setDepth((this.sprite.depth ?? 0) + 3);
    };

    updatePos();
    this.scene.events.on("update", updatePos);

    this.scene.time.delayedCall(duration, () => {
      container.destroy();
      this.scene?.events.off("update", updatePos);
    });
  }

  /** Déposer l’objet à (x,y). */
  public drop(newX: number, newY: number): boolean {
    if (this.currentState !== "carried") return false;

    this.x = newX;
    this.y = newY;

    if (this.sprite) {
      this.sprite.setPosition(newX, newY).setVisible(true).setAlpha(1);
      const body = this.sprite.body as Phaser.Physics.Arcade.Body | undefined;
      if (body) body.enable = true;
    }

    this.currentState = "idle";
    return true;
  }

  /** Jeter l’objet dans une direction/distance. */
  public throw(angleDegree: number, distancePx: number): boolean {
    if (this.currentState !== "carried") return false;

    this.currentState = "thrown";
    const angleRad = Phaser.Math.DegToRad(angleDegree);
    const dx = Math.cos(angleRad) * distancePx;
    const dy = Math.sin(angleRad) * distancePx;

    const targetX = this.x + dx;
    const targetY = this.y + dy;

    if (this.sprite && this.scene) {
      this.sprite.setVisible(true).setAlpha(1);
      const body = this.sprite.body as Phaser.Physics.Arcade.Body | undefined;
      if (body) body.enable = true;

      this.scene.tweens.add({
        targets: this.sprite,
        x: targetX,
        y: targetY,
        duration: Math.max(120, Math.min(400, distancePx)),
        ease: "Linear",
        onComplete: () => {
          this.x = targetX;
          this.y = targetY;
          this.currentState = "idle";
        },
      });
    } else {
      this.x = targetX;
      this.y = targetY;
      this.currentState = "idle";
    }

    return true;
  }

  private setAnimation(state: ObjectState) {
    this.currentState = state;
    if (!this.sprite) return;
    // Always use the resolved key (falls back to idPhaser if alias created)
    const key = this._resolvedTextureKey ?? this.idPhaser;
    this.sprite.setTexture(key);
  }

  /** Détruire l’objet proprement. */
  public destroy(fromSprite = false) {
    if (this._isDestroying) return;
    this._isDestroying = true;

    if (this.scene && this.boundUpdateForLabel) {
      this.scene.events.off("update", this.boundUpdateForLabel);
      this.boundUpdateForLabel = undefined;
    }
    this.adminLabel?.destroy();
    this.adminLabel = undefined;

    if (!fromSprite) {
      this.sprite?.destroy(true);
    }
    if (this.scene && this.textureAddHandler) {
      this.scene.textures.off(Phaser.Textures.Events.ADD, this.textureAddHandler);
      this.textureAddHandler = undefined;
    }
    if (this.scene && this.placeholderTextureKey && this.scene.textures.exists(this.placeholderTextureKey)) {
      this.scene.textures.remove(this.placeholderTextureKey);
    }
    this.sprite = undefined;
    this.scene = undefined;
  }

  /** Téléporte l’objet à (x,y). */
  public moveToforObject(x: number, y: number): void {
    this.x = x;
    this.y = y;
    this.sprite?.setPosition(x, y);
  }

  /** Change la taille de l’objet (+ hitbox). */
  public changeSizeforObject(newWidth: number, newHeight: number): void {
    this.width = newWidth;
    this.height = newHeight;

    if (this.sprite) {
      this.sprite.setDisplaySize(newWidth, newHeight);
      setArcadeBodySize(this.sprite as any, newWidth, newHeight, "topLeft");
    }
  }
}
